import { useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { BLOG_STALE_TIME, blogApi, type BlogLang } from '../../api/blog';
import { LandingShell, LandingTop, useLandingPrefix } from '../../components/landing/LandingShell';
import { PostCard } from '../../components/landing/PostCard';
import { initialIndex } from '../../lib/initialData';
import { useDocumentHead } from '../../lib/useDocumentHead';
import NotFoundPage from '../NotFoundPage';

export default function BlogIndexPage() {
  const { t, i18n } = useTranslation();
  const { tag = null } = useParams();
  const [params] = useSearchParams();
  const prefix = useLandingPrefix();

  const lang: BlogLang = prefix === '/en' ? 'en' : 'bg';
  const locale = lang === 'en' ? 'en-GB' : 'bg-BG';
  const page = Math.max(1, Number(params.get('page')) || 1);

  // What the server rendered this page from, when this is the page it rendered.
  const initial = initialIndex(lang, tag, page);

  const posts = useQuery({
    queryKey: ['blog-index', lang, tag, page],
    queryFn: () => blogApi.posts(lang, page, tag),
    initialData: initial?.posts,
    staleTime: BLOG_STALE_TIME,
  });

  const tags = useQuery({
    queryKey: ['blog-tags', lang],
    queryFn: () => blogApi.tags(lang),
    initialData: initial?.tags,
    staleTime: BLOG_STALE_TIME,
  });

  const currentTag = tag ? tags.data?.find((candidate) => candidate.slug === tag) : undefined;
  const basePath = tag ? `${prefix}/blog/tag/${tag}` : `${prefix}/blog`;
  const pagePath = (n: number) => (n > 1 ? `${basePath}?page=${n}` : basePath);

  const heading = currentTag?.name ?? t('blog.title');
  const description = currentTag ? t('blog.tagLead', { tag: currentTag.name }) : t('blog.lead');

  const head = useMemo(
    () => ({
      title: `${currentTag?.name ?? t('blog.eyebrow')} — Danak`,
      description,
      canonical: pagePath(page),
      noIndex: posts.data?.totalCount === 0,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pagePath is derived from the listed values
    [currentTag?.name, description, basePath, page, posts.data?.totalCount, i18n.resolvedLanguage],
  );

  useDocumentHead(head);

  // A tag nobody has written under is not a page. Known only once the vocabulary has loaded.
  if (tag && tags.data && !currentTag) {
    return <NotFoundPage />;
  }

  return (
    // The index exists in both languages, so the switcher can simply mirror the URL — but not
    // from a tag page, whose tag may have nothing under it in the other language.
    <LandingShell alternatePath={tag ? (lang === 'en' ? '/blog' : '/en/blog') : undefined}>
      <LandingTop>
        <section className="l-hero l-hero--compact">
          <div>
            <p className="l-eyebrow l-eyebrow--onDark">
              {tag ? <Link to={`${prefix}/blog`}>{t('blog.eyebrow')}</Link> : t('blog.eyebrow')}
            </p>
            <h1>{heading}</h1>
            <p className="l-hero-sub">{description}</p>
          </div>
        </section>
      </LandingTop>

      <section className="l-section">
        {tags.data && tags.data.length > 0 && (
          <nav className="l-tag-bar" aria-label={t('blog.topics')}>
            <Link className={tag ? '' : 'is-active'} to={`${prefix}/blog`}>
              {t('blog.allPosts')}
            </Link>
            {/* Only topics with an article in this language. A chip for an empty one links to a
                page that is noindex by design, and Search Console lists every such link it
                follows as "Excluded by noindex" — the English bar linked all fifteen. The page
                being viewed keeps its chip even if empty, so the bar still says where you are. */}
            {tags.data.filter((candidate) => candidate.count > 0 || candidate.slug === tag).map((candidate) => (
              <Link
                key={candidate.slug}
                className={candidate.slug === tag ? 'is-active' : ''}
                to={`${prefix}/blog/tag/${candidate.slug}`}
              >
                {candidate.name}
              </Link>
            ))}
          </nav>
        )}

        {posts.data && posts.data.items.length === 0 && <p className="l-lead">{t('blog.empty')}</p>}

        <div className="l-post-grid">
          {posts.data?.items.map((post) => <PostCard key={post.slug} post={post} prefix={prefix} locale={locale} />)}
        </div>

        {posts.data && (posts.data.hasPrevious || posts.data.hasNext) && (
          // Links, not buttons: each page of the index is a URL a crawler can follow and a person
          // can bookmark.
          <nav className="l-pager" aria-label={t('blog.pages')}>
            {posts.data.hasPrevious ? (
              <Link rel="prev" to={pagePath(page - 1)}>
                ← {t('blog.newer')}
              </Link>
            ) : (
              <span />
            )}
            {posts.data.hasNext && (
              <Link rel="next" to={pagePath(page + 1)}>
                {t('blog.older')} →
              </Link>
            )}
          </nav>
        )}
      </section>
    </LandingShell>
  );
}
